import { fromJS, List, Map } from 'immutable';
import { Delta, PushEntity, PushEvent, PushPayload } from '../../dto/PushEnvelope';
import { Bet, DetailMarket, MarketCategory, Mbm, TurboLiveEntity } from '../../dto';
import {
    createOrderedIndex,
    indexById,
    insertToOrderedIndex,
    removeFromOrderedIndex,
    updateIndexedById
} from '../indexingUtils';
import { MatchDetailSnapshot, MatchMbmSnapshot } from '../../dto/SnapshotEnvelope';
import { getCombinableLiveBets, isNewBetCombinable } from '../../../combinable-bets/betExclusionValidations';
import { isProfileCombinableBetsEnabled } from '../../../../profiles';
import fp from 'lodash/fp';

export const matchDetailSnapshot = (snapshot: MatchDetailSnapshot) => db => {
    const combinableBets = isProfileCombinableBetsEnabled()
        ? getCombinableLiveBets(
              snapshot.bets,
              snapshot.markets,
              snapshot.tournamentAllowMarketCombinations,
              snapshot.matchId
          )
        : null;

    return db
        .setIn(['turboLive', 'detail', 'matchId'], snapshot.matchId)
        .setIn(
            ['turboLive', 'detail', 'tournamentAllowMarketCombinations'],
            snapshot.tournamentAllowMarketCombinations || false
        )
        .setIn(['turboLive', 'detail', 'markets'], indexById(snapshot.markets))
        .setIn(['turboLive', 'detail', 'marketIdsByOrder'], createOrderedIndex(snapshot.markets, 'order'))
        .setIn(['turboLive', 'detail', 'marketCategories'], indexById(snapshot.categories))
        .setIn(['turboLive', 'detail', 'marketCategoryIdsByOrder'], createOrderedIndex(snapshot.categories))
        .setIn(['turboLive', 'detail', 'bets'], indexById(snapshot.bets))
        .setIn(['turboLive', 'detail', 'betIdsByMarket'], indexOrderedBetsByMarket(snapshot.bets))
        .setIn(
            ['turboLive', 'detail', 'combinableBetIdsByMarket'],
            combinableBets ? indexOrderedBetsByMarket(combinableBets) : Map()
        )
        .setIn(['turboLive', 'detail', 'isLive'], true);
};

export const matchDetailPush = (push: PushPayload) => db => {
    switch (push.t) {
        case PushEntity.BET:
            return db.updateIn(['turboLive', 'detail'], onDetailBetPush(push.e, push.d));
        case PushEntity.MARKET:
            return db.updateIn(['turboLive', 'detail'], onDetailMarketPush(push.e, push.d));
        case PushEntity.MARKET_CATEGORY:
            return db.updateIn(['turboLive', 'detail'], onMarketCategoryPush(push.e, push.d));
        default:
            console.error(`Unsupported PushEntity ${push.t}`);
            return db;
    }
};

export const matchDetailBetsClear = db => {
    return db
        .deleteIn(['turboLive', 'detail', 'matchId'])
        .deleteIn(['turboLive', 'detail', 'markets'])
        .deleteIn(['turboLive', 'detail', 'tournamentAllowMarketCombinations'])
        .deleteIn(['turboLive', 'detail', 'marketIdsByOrder'])
        .deleteIn(['turboLive', 'detail', 'bets'])
        .deleteIn(['turboLive', 'detail', 'betIdsByMarket'])
        .deleteIn(['turboLive', 'detail', 'marketCategories'])
        .deleteIn(['turboLive', 'detail', 'marketCategoryIdsByOrder'])
        .deleteIn(['turboLive', 'detail', 'selectedMarketCategoryId'])
        .deleteIn(['turboLive', 'detail', 'combinableBetIdsByMarket'])
        .deleteIn(['turboLive', 'detail', 'isLive']);
};

export const matchDetailSelectCategory = (marketCategoryId: string | null) => db => {
    return db.setIn(['turboLive', 'detail', 'selectedMarketCategoryId'], marketCategoryId);
};

export const matchDetailMbmSnapshot = (snapshot: MatchMbmSnapshot) => db => {
    const hasMatchMbm = snapshot !== undefined && snapshot.mbms?.length > 0;

    return hasMatchMbm
        ? db
              .setIn(['turboLive', 'detail', 'mbm'], indexById(snapshot.mbms))
              .setIn(
                  ['turboLive', 'detail', 'mbmIdsByOrder'],
                  groupMbm(snapshot.mbms).map(group => createOrderedIndex(group, 'order'))
              )
              .setIn(['turboLive', 'detail', 'mbmLoaded'], true)
        : db
              .deleteIn(['turboLive', 'detail', 'mbm'])
              .deleteIn(['turboLive', 'detail', 'mbmIdsByOrder'])
              .setIn(['turboLive', 'detail', 'mbmLoaded'], true);
};

function groupMbm(list) {
    return list.reduce((groups, item) => {
        const group = groups?.get(item.periodOrder) || [];
        group.push(item);
        return groups.set(item.periodOrder, group);
    }, Map());
}

export const matchDetailMbmPush = (push: PushPayload) => db => {
    return db.updateIn(['turboLive', 'detail'], onMbmPush(push.e, push.d));
};

function onDetailBetPush(event: PushEvent, data: Bet | Delta | { id: string }) {
    switch (event) {
        case PushEvent.CREATED:
            return onBetCreated(data as Bet);
        case PushEvent.UPDATED:
            return updateIndexedById(data as Delta, 'bets', 'detail/bets');
        case PushEvent.DELETED:
            return onBetDeleted(data.id);
        default:
            console.error(`Invalid PushEvent ${event}`);
            return turboLiveDb => turboLiveDb;
    }
}

function onDetailMarketPush(event: PushEvent, data: DetailMarket | Delta | { id: string }) {
    switch (event) {
        case PushEvent.CREATED:
            return onMarketCreated(data as DetailMarket);
        case PushEvent.UPDATED:
            return updateIndexedById(data as Delta, 'markets');
        case PushEvent.DELETED:
            return onMarketDeleted(data.id);
        default:
            console.error(`Invalid PushEvent ${event}`);
            return turboLiveDb => turboLiveDb;
    }
}

function onMarketCategoryPush(event: PushEvent, data: MarketCategory | Delta | { id: string }) {
    switch (event) {
        case PushEvent.CREATED:
            return onMarketCategoryCreated(data as MarketCategory);
        case PushEvent.UPDATED:
            return updateIndexedById(data as Delta, 'marketCategories');
        case PushEvent.DELETED:
            return onMarketCategoryDeleted(data.id);
        default:
            return turboLiveDb => turboLiveDb;
    }
}

function onMbmPush(event: PushEvent, data: MarketCategory | Delta | { id: string }) {
    switch (event) {
        case PushEvent.CREATED:
            return onMbmCreated(data as Mbm);
        case PushEvent.UPDATED:
            return updateIndexedById(data as Delta, 'mbm');
        case PushEvent.DELETED:
            return onMbmDeleted(data.id);
        default:
            console.error(`Invalid PushEvent ${event}`);
            return turboLiveDb => turboLiveDb;
    }
}

function onBetCreated(bet: Bet) {
    return turboLiveDb => {
        const { id, marketId, order, matchId } = bet;
        let newDb = turboLiveDb;
        newDb = newDb
            .update('bets', bets => bets.set(id, fromJS(bet)))
            .update('betIdsByMarket', Map(), marketIds =>
                marketIds.update(marketId, List(), insertToOrderedIndex({ id, order }))
            );

        if (isProfileCombinableBetsEnabled()) {
            const markets = turboLiveDb.get('markets');
            const bets = turboLiveDb.get('bets');
            const jsBets = bets
                ?.map(bet => bet.toJS())
                .toIndexedSeq()
                .toArray();
            const jsMarkets = markets
                ?.map(market => market.toJS())
                .toIndexedSeq()
                .toArray();
            const allowMarketCombinations = turboLiveDb.get('tournamentAllowMarketCombinations');
            const isCombinable = isNewBetCombinable(bet, jsBets, jsMarkets, allowMarketCombinations, matchId);
            if (isCombinable) {
                newDb = newDb.update('combinableBetIdsByMarket', Map(), marketIds =>
                    marketIds.update(marketId, List(), insertToOrderedIndex({ id, order }))
                );
            }
        }

        return newDb;
    };
}

function onBetDeleted(betId: string) {
    return turboLiveDb => {
        const bet = turboLiveDb.get('bets').get(betId);
        if (!bet) {
            console.warn(`Bet ${betId} not found, cannot handle detail DELETE push event.`, { turboLiveDb });
            return turboLiveDb;
        }
        const marketId = bet.get('marketId');
        let newDb = turboLiveDb;

        if (isProfileCombinableBetsEnabled()) {
            newDb = newDb.update('combinableBetIdsByMarket', Map(), marketIds =>
                marketIds.update(marketId, List(), removeFromOrderedIndex(betId))
            );
        }

        return newDb
            .update('bets', bets => bets.delete(betId))
            .update('betIdsByMarket', Map(), marketIds =>
                marketIds.update(marketId, List(), removeFromOrderedIndex(betId))
            );
    };
}

function onMarketCreated(market: DetailMarket) {
    return turboLiveDb => {
        const { id, order } = market;

        return turboLiveDb
            .update('markets', markets => markets.set(id, fromJS(market)))
            .update('marketIdsByOrder', List(), insertToOrderedIndex({ id, order }));
    };
}

function onMarketDeleted(marketId: string) {
    return turboLiveDb => {
        return turboLiveDb
            .update('markets', markets => markets.delete(marketId))
            .update('marketIdsByOrder', List(), removeFromOrderedIndex(marketId));
    };
}

function onMarketCategoryCreated(marketCategory: MarketCategory) {
    return turboLiveDb => {
        const { id, order } = marketCategory;

        return turboLiveDb
            .update('marketCategories', entities => entities?.set(id, fromJS(marketCategory)))
            .update('marketCategoryIdsByOrder', List(), insertToOrderedIndex({ id, order }));
    };
}

function onMarketCategoryDeleted(marketCategoryId: string) {
    return turboLiveDb => {
        return turboLiveDb
            .update('marketCategories', entity => entity.delete(marketCategoryId))
            .update('marketCategoryIdsByOrder', List(), removeFromOrderedIndex(marketCategoryId));
    };
}

function onMbmCreated(mbm: Mbm) {
    return turboLiveDb => {
        const { id, order, periodOrder } = mbm;

        return turboLiveDb
            .update('mbm', entities => entities?.set(id, fromJS(mbm)))
            .updateIn(['mbmIdsByOrder', periodOrder], List(), insertToOrderedIndex({ id, order }));
    };
}

function onMbmDeleted(mbmId: string) {
    return turboLiveDb => {
        const periodOrder = turboLiveDb.getIn(['mbm', mbmId, 'periodOrder']);
        return turboLiveDb
            .update('mbm', entity => entity.delete(mbmId))
            .updateIn(['mbmIdsByOrder', periodOrder], List(), removeFromOrderedIndex(mbmId));
    };
}

export function indexOrderedBetsByMarket(bets: Bet[], attribute?: string, idAttr?: string) {
    const composeBets = fp.compose(
        fp.mapValues((item: TurboLiveEntity[]) => createOrderedIndex(item, attribute, idAttr)),
        fp.groupBy('marketId')
    );

    return Map(composeBets(bets));
}
